<template>
  <div class="training-map" :class="{ 'is-isometric': isIsometric, 'is-immersive': immersive }" :data-map-view="isIsometric ? 'ISOMETRIC' : 'GRID'">
    <button v-if="immersive && showToolbar" class="student-map-settings-toggle" type="button" aria-label="地图设置" title="地图设置" :aria-expanded="toolbarOpen" @click="toolbarOpen = !toolbarOpen">地图设置 {{ toolbarOpen ? '−' : '+' }}</button>
    <div v-if="showToolbar" v-show="!immersive || toolbarOpen" class="training-map-toolbar">
      <div class="training-layer-options">
        <span class="training-map-label">图层：</span>
        <label v-for="layer in layerOptions" :key="layer.key">
          <input :checked="layers[layer.key]" type="checkbox" @change="changeLayer(layer.key, $event.target.checked)">{{ layer.name }}
        </label>
      </div>
      <div class="training-map-controls">
        <div v-if="allowViewSwitch" class="training-map-view-switch" role="group" aria-label="地图视角">
          <button class="btn small" type="button" :class="{ active: !isIsometric }" :aria-pressed="!isIsometric" @click="changeView('GRID')" @keydown.enter.prevent="changeView('GRID')" @keydown.space.prevent="changeView('GRID')">网格地图</button>
          <button class="btn small" type="button" :class="{ active: isIsometric }" :aria-pressed="isIsometric" @click="changeView('ISOMETRIC')" @keydown.enter.prevent="changeView('ISOMETRIC')" @keydown.space.prevent="changeView('ISOMETRIC')">2.5D 城市</button>
        </div>
        <button v-if="isIsometric" class="btn small training-map-motion-toggle" type="button" :disabled="cityReducedMotion" :aria-pressed="cityActivityStopped" :title="cityReducedMotion ? '已遵循系统的减少动态效果设置' : '暂停或继续行人和车辆动画'" @click="cityActivityPaused = !cityActivityPaused">{{ cityActivityLabel }}</button>
        <select v-if="showModeSelect" :value="localMode" class="field" aria-label="地图显示模式" @change="changeMode($event.target.value)">
          <option v-for="option in mapModeOptions" :key="option.value" :value="option.value">{{ option.name }}</option>
        </select>
        <span class="training-map-label">缩放 {{ zoomDisplay }}×</span>
        <button class="btn small" :disabled="zoom <= minZoom" aria-label="缩小地图" @click="stepCameraZoom(-1)">－</button>
        <button class="btn small" @click="resetView">复位</button>
        <button class="btn small" :disabled="zoom >= maxZoom" aria-label="放大地图" @click="stepCameraZoom(1)">＋</button>
      </div>
    </div>
    <div ref="mapViewport" class="training-map-viewport" :class="{ 'is-panning': (cameraDrag && cameraDrag.moved) || (flatMapDrag && flatMapDrag.moved) }" @click="inspect" @keydown.enter.prevent="inspect" @keydown.space.prevent="inspect" @keydown.esc.prevent="closeMapSelection" @pointerdown="startTrainingPointer" @pointermove="moveTrainingPointer" @pointerup="finishTrainingPointer" @pointercancel="finishTrainingPointer" @lostpointercapture="finishTrainingPointer" @pointerleave="leaveTrainingViewport" @wheel="wheelCameraZoom">
      <!-- SVG is generated locally from versioned map data; all external text is escaped by the renderer. -->
      <div ref="mapStage" class="training-map-stage" :style="[mapStageStyle, immersive && !isIsometric ? { height: (zoom * 100) + '%' } : {}]" v-html="markup"></div>
      <div v-if="immersive" class="training-map-bottom-tools">
        <span class="training-map-gesture-hint" aria-hidden="true">{{ isIsometric ? '按住拖动 · 滚轮缩放 · 点击查看详情' : '按住拖动 · 使用地图设置缩放 · 点击查看详情' }}</span>
        <isometric-map-scale v-if="isIsometric" :metrics="renderedMap.scale" :scene-width="cameraViewportWidth" :zoom="zoom" />
      </div>
      <isometric-map-scale v-else-if="isIsometric" :metrics="renderedMap.scale" :scene-width="cameraViewportWidth" :zoom="zoom" />
      <section v-if="isIsometric && selection" class="training-map-detail" :style="cityDetailPosition" role="region" aria-label="地图对象详情" aria-live="polite" @click.stop @pointerdown.stop @wheel.stop @keydown.stop="detailKeydown">
        <div class="training-map-detail-heading"><span>{{ selection.badge }}</span><button class="training-map-detail-close" type="button" aria-label="关闭地图详情" @click="closeMapSelection">×</button></div>
        <h3>{{ selection.title }}</h3>
        <dl><div v-for="item in selection.fields" :key="item.label"><dt>{{ item.label }}</dt><dd>{{ item.value }}</dd></div></dl>
        <p v-if="selection.description" class="training-map-detail-note">{{ selection.description }}</p>
        <template v-if="selection.kind === 'patrol'">
          <button class="btn small" type="button" @click="patrolGreeting = !patrolGreeting">{{ patrolGreeting ? '收起招呼' : '打个招呼' }}</button>
          <p v-if="patrolGreeting" class="training-map-patrol-reply">你好，欢迎来到社区！</p>
        </template>
        <slot name="selection-actions" :selection="selection" :close="closeMapSelection"></slot>
      </section>
      <span v-if="isIsometric && !immersive" class="training-map-gesture-hint" aria-hidden="true">按住拖动 · 滚轮缩放 · 点击查看详情</span>
    </div>
    <div v-if="isIsometric && showLegend" class="training-map-isometric-legend" v-html="renderedMap.legend"></div>
    <p v-if="unlocatedIncidentCount" class="training-map-location-note" role="status">{{ unlocatedIncidentCount }} 个已释放案件暂无地图位置，可从案件列表查看和处置。</p>
    <div v-if="selection && !isIsometric" class="training-map-selection" aria-live="polite">
      <div><strong>{{ selection.title }}</strong><span>{{ selection.detail }}</span></div>
      <button class="btn small" @click="selection = null">关闭</button>
    </div>
  </div>
</template>

<script>
import { createPrototypeMapRenderer } from './maps/prototypeMapRenderer'
import { createIsometricCityRenderer } from './maps/isometricCityRenderer'
import IsometricMapScale from './IsometricMapScale.vue'
import isometricMapCamera from '../mixins/isometricMapCamera'
import isometricCityInteraction from '../mixins/isometricCityInteraction'
import { inspectMapPart, inspectMapCell, inspectMapLandmark, inspectMapGrid, inspectMapIncident } from '../services/mapInspectionDetails'
import { locateMapIncidents, incidentClosed } from '../services/mapRuntimeGeometry'
import { mapDisplayModes, visibleMapMode } from '../services/mapDisplayModes'
import '../assets/isometric-city.css'

export default {
  name: 'TrainingMap',
  components: { IsometricMapScale },
  mixins: [isometricMapCamera, isometricCityInteraction],
  props: {
    template: { type: Object, required: true },
    mode: { type: String, default: 'ZONE' },
    incidents: { type: Array, default: () => [] },
    gridPlans: { type: Array, default: () => [] },
    pendingCellIds: { type: Array, default: () => [] },
    selectedCellIds: { type: Array, default: () => [] },
    collectedParts: { type: Array, default: () => [] },
    showToolbar: { type: Boolean, default: true }, showLegend: { type: Boolean, default: true }, immersive: Boolean,
    showModeSelect: { type: Boolean, default: true },
    emitOnly: { type: Boolean, default: false },
    zoomStep: { type: Number, default: 0.5 },
    zoomValue: { type: Number, default: null },
    layerSettings: { type: Object, default: null },
    allowViewSwitch: { type: Boolean, default: false },
    viewValue: { type: String, default: null },
    activeGridCode: { type: String, default: '' },
    activeCaseId: { type: String, default: '' },
    showGridLabels: { type: Boolean, default: false },
    personnel: { type: Array, default: () => [] },
    caseFlow: { type: Array, default: () => [] }
  },
  data () {
    return {
      localZoom: 1, selection: null, localMode: this.mode, localView: 'GRID', toolbarOpen: false, flatMapDrag: null,
      localLayers: { bld: true, road: true, water: true, part: true, cs: true, label: true, ref: false, ...(this.layerSettings || {}) },
      layerOptions: [
        { key: 'bld', name: '街区建筑' }, { key: 'road', name: '路网' }, { key: 'water', name: '水体' },
        { key: 'part', name: '部件点位' }, { key: 'cs', name: '案件点位' },
        { key: 'label', name: '地标标注' }, { key: 'ref', name: '参考网格对照' }
      ]
    }
  },
  computed: {
    mapModeOptions () { return mapDisplayModes(this.template.layers) },
    zoom: { get () { return this.zoomValue == null ? this.localZoom : this.zoomValue }, set (value) { this.localZoom = value; this.$emit('update:zoomValue', value) } },
    layers () { return this.localLayers },
    isIsometric () { return this.allowViewSwitch && (this.viewValue || this.localView) === 'ISOMETRIC' },
    mapIncidents () { return locateMapIncidents(this.template, this.incidents.filter(item => item.released !== false), this.gridPlans) },
    unlocatedIncidentCount () { return this.mapIncidents.filter(item => !item.mapLocated).length },
    renderer () { return this.isIsometric ? createIsometricCityRenderer(this.template) : createPrototypeMapRenderer(this.template) },
    renderedMap () {
      const mode = { ZONE: 'zone', POPULATION: 'pop', ECONOMY: 'eco', RISK: 'risk', GRID: 'grid', BASE: 'base' }[this.localMode] || 'zone'
      const visible = this.mapIncidents.filter(item => item.mapLocated)
      const state = {
        grids: this.gridPlans.length ? this.gridPlans.map(grid => ({ ...grid, cells: grid.cellIds || grid.cells || [] })) : (this.selectedCellIds.length ? [{ code: 'A', name: '当前规划', cells: this.selectedCellIds }] : []),
        space: this.collectedParts, released: visible.map(item => item.id),
        prog: visible.reduce((result, item) => { result[item.id] = { closed: incidentClosed(item) }; return result }, {})
      }
      const options = { layers: this.layers, zoom: 1, caseObj: { cases: visible }, showGridLabels: this.showGridLabels, activeGridCode: this.activeGridCode }
      if (this.isIsometric) {
        const frame = this.renderer.renderFrame(mode, state, { ...options, fixedScale: true })
        return { ...frame, html: `<div class="map-box isometric-city"><svg viewBox="0 0 ${frame.width} ${frame.height}" data-isometric-map="1" role="img" aria-label="辖区2.5D城市地图">${frame.svg}</svg></div>` }
      }
      return { html: this.renderer.render(mode, state, options) }
    },
    markup () {
      // A map in the case library and a map in the workbench may coexist in future composite views.
      return this.renderedMap.html.replace(/data-cell="([^"]+)"/g, (match, id) => match + (this.pendingCellIds.includes(id) ? ' data-pending-cell="1"' : '')).replace(/\sid="([^"]+)"/g, (_, id) => ` id="training-map-${this._uid}-${id}"`)
        .replace(/url\(#([^\)]+)\)/g, (_, id) => `url(#training-map-${this._uid}-${id})`)
    }
  },
  watch: {
    mode (value) { this.changeMode(value) },
    mapModeOptions: { immediate: true, handler () { this.changeMode(this.localMode) } },
    viewValue (value, previous) { if (value !== previous) this.resetView() },
    gridPlans () { this.refreshMapSelection() },
    incidents () { this.refreshMapSelection() },
    collectedParts () { this.refreshMapSelection() },
    layerSettings: { deep: true, handler (value) { if (value) Object.keys(this.localLayers).forEach(key => { if (key in value) this.localLayers[key] = value[key] }) } },
    template (value, previous) { if (!previous || value.id !== previous.id || value.code !== previous.code) this.resetView() }
  },
  methods: {
    startTrainingPointer (event) {
      if (!this.immersive || this.isIsometric) return this.startCameraDrag(event)
      if (event.button !== 0 || event.isPrimary === false || this.flatMapDrag) return
      const viewport = this.$refs.mapViewport
      this.cameraBlockClickUntil = 0
      this.flatMapDrag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop, moved: false }
    },
    moveTrainingPointer (event) {
      if (!this.flatMapDrag) return this.moveCameraDrag(event)
      const drag = this.flatMapDrag
      if (event.pointerId !== drag.id) return
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y
      if (!drag.moved && Math.hypot(dx, dy) < 4) return
      if (!drag.moved) { drag.moved = true; try { this.$refs.mapViewport.setPointerCapture(drag.id) } catch (_) {} }
      event.preventDefault()
      this.$refs.mapViewport.scrollLeft = drag.left - dx
      this.$refs.mapViewport.scrollTop = drag.top - dy
    },
    finishTrainingPointer (event) {
      const drag = this.flatMapDrag
      if (!drag) return this.finishCameraDrag(event)
      if (event && event.pointerId !== drag.id) return
      if (drag.moved) this.cameraBlockClickUntil = Date.now() + 300
      this.flatMapDrag = null
      try { if (this.$refs.mapViewport?.hasPointerCapture(drag.id)) this.$refs.mapViewport.releasePointerCapture(drag.id) } catch (_) {}
    },
    leaveTrainingViewport () { if (this.flatMapDrag) { if (!this.flatMapDrag.moved) this.finishTrainingPointer(); return }; this.leaveCameraViewport() },
    changeView (value) { if ((this.viewValue || this.localView) === value) return; this.localView = value; this.$emit('update:viewValue', value); this.resetView() },
    changeMode (value) { const next = visibleMapMode(this.template.layers, value); if (next !== this.localMode) { this.localMode = next; this.$emit('update:mode', next) } },
    changeLayer (key, value) { this.localLayers[key] = value; this.$emit('layers-change', { ...this.localLayers }) },
    resetView () { this.zoom = 1; this.selection = null; this.patrolGreeting = false; this.resetCamera() },
    refreshMapSelection () {
      if (!this.selection) return
      const { kind, key } = this.selection
      if (kind === 'case') { const item = this.mapIncidents.find(c => String(c.id) === key); this.selection = item ? inspectMapIncident(item, this.gridPlans, this.caseFlow) : null }
      if (kind === 'cell') { const cell = (this.template.cells || []).find(c => String(c.id) === key); if (cell) this.selection = inspectMapCell(this.template, cell, this.gridPlans) }
      if (kind === 'grid') { const grid = this.gridPlans.find(g => String(g.code) === key); this.selection = grid ? inspectMapGrid(this.template, grid, this.personnel) : null }
    },
    async focusIncident (id, { recenter = false, scroll = false } = {}) {
      const item = this.mapIncidents.find(c => c.id === id)
      if (!item) return
      if (!this.layers.cs) this.changeLayer('cs', true)
      await this.$nextTick()
      const target = [...this.$refs.mapStage.querySelectorAll('[data-case]')].find(el => el.getAttribute('data-case') === String(id))
      this.showMapSelection(inspectMapIncident(item, this.gridPlans, this.caseFlow), target)
      if (scroll) this.$refs.mapViewport.scrollIntoView({ block: 'center', behavior: 'auto' })
      if (target && recenter && this.isIsometric) {
        const v = this.$refs.mapViewport.getBoundingClientRect(), r = target.getBoundingClientRect(), inset = v.width > 700 ? 165 : 0
        this.cameraX += v.left + (v.width - inset) / 2 - r.left - r.width / 2
        this.cameraY += v.top + v.height / 2 - r.top - r.height / 2
        this.constrainCamera()
      } else if (target && scroll && !this.isIsometric) target.scrollIntoView({ block: 'nearest', inline: 'center' })
      this.positionMapDetail()
    },
    inspect (event) {
      if ((this.isIsometric || this.immersive) && event.type === 'click' && Date.now() < this.cameraBlockClickUntil) { event.preventDefault(); event.stopPropagation(); return }
      const incident = event.target.closest('[data-case]')
      if (incident) {
        const item = this.mapIncidents.find(c => String(c.id) === incident.getAttribute('data-case'))
        if (item) { this.showMapSelection(inspectMapIncident(item, this.gridPlans, this.caseFlow), incident); this.$emit('case-select', this.incidents.find(c => c.id === item.id)) }
        return
      }
      const gridLabel = event.target.closest('[data-map-grid]')
      if (gridLabel) {
        const item = this.gridPlans.find(g => String(g.code) === gridLabel.getAttribute('data-map-grid'))
        if (item) { this.showMapSelection(inspectMapGrid(this.template, item, this.personnel), gridLabel); this.$emit('grid-select', item) }
        return
      }
      const part = event.target.closest('[data-pt]')
      if (part) {
        const item = (this.template.partPoints || []).find(point => point.id === part.getAttribute('data-pt'))
        if (item) {
          this.showMapSelection(inspectMapPart(this.template, item, this.gridPlans), part)
          this.$emit('part-select', item)
        }
        return
      }
      const landmark = event.target.closest('[data-iso-landmark]')
      if (landmark) {
        const index = (this.template.landmarks || []).findIndex((item, i) => String(item.id || 'landmark-' + i) === landmark.getAttribute('data-iso-landmark'))
        if (index >= 0) { const item = this.template.landmarks[index]; this.showMapSelection(inspectMapLandmark(this.template, item, index, this.gridPlans), landmark); this.$emit('landmark-select', item) }
        return
      }
      const patrol = event.target.closest('[data-city-actor]')
      if (patrol) { this.inspectPatrol(patrol); return }
      const cell = event.target.closest('[data-cell]')
      if (!cell) return
      const item = (this.template.cells || []).find(value => value.id === cell.getAttribute('data-cell'))
      if (item) {
        this.showMapSelection(inspectMapCell(this.template, item, this.gridPlans), cell)
        this.$emit('cell-select', item)
      }
    }
  }
}
</script>

